import {
    PublicationSheet as PublicationSheetInterface,
    PublicationSheetWithId,
    TreeData,
    ModelType,
} from '@livv/models';
import { Box } from '@mui/material';
import { useRouter } from 'next/router';
import { FC } from 'react';
import PreviousNextSheetLinks from '../PreviousNextSheetLinks';
import PublicationSheetContent from './PublicationSheetContent';
import DocumentNotFound from '@components/DocumentNotFound';
import DecisionsCitations from '@screens/Books/Book/Nodes/Citations/Decisions';
import ChildNodes from '@screens/Books/Book/Nodes/Previews/ChildNodes';
import ParentNodes from '@screens/Books/Book/Nodes/Previews/ParentNodes';
import { useBookPageContext } from '@utils/context/book';
import useUser from '@utils/context/user';
import isPdfExportAllowed from '@utils/entitlements/pdfExportPolicy';
import { useBookPathSlugs, useBookSheetSlugs } from '@utils/hooks/useBookSlugs';

interface PublicationSheetProps {
    sheet: PublicationSheetInterface;
    sheets: PublicationSheetWithId[];
}

const PublicationSheet: FC<PublicationSheetProps> = ({ sheet, sheets }) => {
    const { bookData } = useBookPageContext();
    const { bookPath } = useBookPathSlugs();
    const { sheetId } = useBookSheetSlugs();
    const { privileges } = useUser(); // intentionally do not wait for loading to avoid content blocking due to user privileges loading
    const router = useRouter();

    const { children: childNodes } = bookData.getNode(bookPath.split('/'));
    const sheetWithId = { ...sheet, id: sheetId } as PublicationSheetWithId;
    const showPrintButton = isPdfExportAllowed(
        ModelType.PUBLICATION_SHEET,
        undefined,
        privileges?.isInternalUser,
    );

    if (!sheet.isPublished) {
        router.push('/404');

        return null;
    }

    return (
        <Box
            sx={{
                maxWidth: (muiT) => muiT.spacing(80),
                ...(showPrintButton // Make text selectable only if print is allowed
                    ? {}
                    : {
                          MozUserSelect: 'none',
                          WebkitUserSelect: 'none',
                          msUserSelect: 'none',
                          userSelect: 'none',
                      }),
            }}
        >
            <PublicationSheetContent sheet={sheetWithId} showPrintButton={showPrintButton} />
            {sheets.length > 0 && <PreviousNextSheetLinks sheets={sheets} />}
            <DecisionsCitations sx={{ mt: 6.25 }} />
            {childNodes && childNodes?.length > 0 && <ChildNodes />}
            <ParentNodes />
        </Box>
    );
};

const PublicationSheetWrapper: FC = () => {
    const { sheetData: sheet, sheets } = useBookPageContext() as {
        bookData: TreeData;
        sheetData: PublicationSheetInterface;
        sheets: PublicationSheetWithId[];
    };

    if (!sheet) {
        return <DocumentNotFound />;
    }

    return <PublicationSheet sheet={sheet} sheets={sheets} />;
};

export default PublicationSheetWrapper;
